import { create } from "zustand";
import type { ZonedTimeString } from "@poi-planner/shared";

// ── Calendar DnD Store ──
// Transient drag state for the time grid: the live drop preview (phantom
// block + snapped time readout). Kept out of uiStore so per-column
// subscriptions stay cheap during drags.

export interface DropPreview {
  date: string; // YYYY-MM-DD of the target column
  startTime: ZonedTimeString; // snapped
  endTime: ZonedTimeString;
  label: string; // e.g. "Ghibli Museum · 2h"
  timeLabel: string; // snapped readout, e.g. "10:35"
}

export interface DateRangePreview {
  startDate: string;
  endDate: string;
  targetDate: string;
  label: string;
  invalid: boolean;
  conflict: boolean;
}

interface CalendarDndState {
  preview: DropPreview | null;
  creationPreview: {
    startTime: ZonedTimeString;
    endTime: ZonedTimeString;
  } | null;
  resizePreview: {
    startTime: ZonedTimeString;
    endTime: ZonedTimeString;
  } | null;
  dateRangePreview: DateRangePreview | null;

  setPreview: (preview: DropPreview | null) => void;
  setCreationPreview: (preview: CalendarDndState["creationPreview"]) => void;
  setResizePreview: (preview: CalendarDndState["resizePreview"]) => void;
  setDateRangePreview: (preview: CalendarDndState["dateRangePreview"]) => void;
}

export const useCalendarDndStore = create<CalendarDndState>((set) => ({
  preview: null,
  creationPreview: null,
  resizePreview: null,
  dateRangePreview: null,

  setPreview: (preview) =>
    set((state) => {
      // Avoid re-render churn when the snapped preview hasn't moved
      if (
        state.preview &&
        preview &&
        state.preview.date === preview.date &&
        state.preview.startTime === preview.startTime &&
        state.preview.endTime === preview.endTime
      ) {
        return state;
      }
      return { preview };
    }),
  setCreationPreview: (creationPreview) => set({ creationPreview }),
  setResizePreview: (resizePreview) => set({ resizePreview }),
  setDateRangePreview: (dateRangePreview) => set({ dateRangePreview }),
}));
